import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/Navbar';
import { 
  Ticket, Plus, Trash2, Edit, Check, X, Loader2, 
  Percent, DollarSign, Calendar, Hash, Copy, ToggleLeft, ToggleRight 
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const SellerCoupons = () => {
  const { user } = useAuth();
  const [coupons, setCoupons] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    min_purchase: '',
    max_uses: '',
    expires_at: '',
    description: ''
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      const response = await axios.get(`${API_URL}/seller/coupons`, { 
        withCredentials: true 
      });
      setCoupons(response.data.coupons || []);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Erro ao carregar cupons:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      discount_type: 'percentage',
      discount_value: '',
      min_purchase: '',
      max_uses: '',
      expires_at: '',
      description: ''
    });
    setEditingCoupon(null);
  };

  const handleOpenForm = (coupon = null) => {
    if (coupon) {
      setEditingCoupon(coupon);
      setFormData({
        code: coupon.code,
        discount_type: coupon.discount_type,
        discount_value: coupon.discount_value.toString(),
        min_purchase: coupon.min_purchase?.toString() || '',
        max_uses: coupon.max_uses?.toString() || '',
        expires_at: coupon.expires_at ? coupon.expires_at.split('T')[0] : '',
        description: coupon.description || ''
      });
    } else {
      resetForm();
    }
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.code || !formData.discount_value) {
      toast.error('Preencha código e valor do desconto');
      return;
    }

    setSaving(true);
    
    const payload = {
      code: formData.code.toUpperCase(),
      discount_type: formData.discount_type,
      discount_value: parseFloat(formData.discount_value),
      min_purchase: formData.min_purchase ? parseFloat(formData.min_purchase) : 0,
      max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
      expires_at: formData.expires_at ? new Date(formData.expires_at).toISOString() : null,
      description: formData.description || null
    };

    try {
      if (editingCoupon) {
        await axios.put(
          `${API_URL}/seller/coupons/${editingCoupon.coupon_id}`,
          payload,
          { withCredentials: true }
        );
        toast.success('Cupom atualizado!');
      } else {
        await axios.post(
          `${API_URL}/seller/coupons`,
          payload,
          { withCredentials: true }
        );
        toast.success('Cupom criado!');
      }
      
      setShowForm(false);
      resetForm();
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar cupom');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (coupon) => {
    try {
      await axios.put(
        `${API_URL}/seller/coupons/${coupon.coupon_id}`,
        { active: !coupon.active },
        { withCredentials: true }
      );
      toast.success(coupon.active ? 'Cupom desativado' : 'Cupom ativado');
      fetchCoupons();
    } catch (error) {
      toast.error('Erro ao atualizar cupom');
    }
  };

  const handleDelete = async (couponId) => {
    if (!window.confirm('Tem certeza que deseja excluir este cupom?')) return;
    
    try {
      await axios.delete(`${API_URL}/seller/coupons/${couponId}`, { 
        withCredentials: true 
      });
      toast.success('Cupom excluído');
      fetchCoupons();
    } catch (error) {
      toast.error('Erro ao excluir cupom');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Código copiado!');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Sem expiração';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-rose-500 rounded-xl flex items-center justify-center">
              <Ticket className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900" data-testid="coupons-title">
                Cupons de Desconto
              </h1>
              <p className="text-gray-600">Crie cupons para atrair mais clientes</p>
            </div>
          </div>

          <Button
            onClick={() => handleOpenForm()}
            className="bg-gradient-to-r from-pink-500 to-rose-500"
            data-testid="create-coupon-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Cupom
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl border p-4">
            <p className="text-sm text-gray-500">Total de Cupons</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total_coupons || 0}</p>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <p className="text-sm text-gray-500">Cupons Ativos</p>
            <p className="text-2xl font-bold text-emerald-600">{stats.active_coupons || 0}</p>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <p className="text-sm text-gray-500">Total de Usos</p>
            <p className="text-2xl font-bold text-indigo-600">{stats.total_uses || 0}</p>
          </div>
        </div>

        {/* Coupons List */}
        {coupons.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border">
            <Ticket className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Nenhum cupom criado
            </h2>
            <p className="text-gray-600 mb-4">
              Crie cupons de desconto para aumentar suas vendas
            </p>
            <Button onClick={() => handleOpenForm()}>
              <Plus className="w-4 h-4 mr-2" />
              Criar Primeiro Cupom
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {coupons.map((coupon) => (
              <div
                key={coupon.coupon_id}
                className={`bg-white rounded-xl border p-6 ${
                  !coupon.active || coupon.is_expired ? 'opacity-60' : ''
                }`}
                data-testid={`coupon-${coupon.coupon_id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Code and badges */}
                    <div className="flex items-center gap-3 mb-2">
                      <div 
                        className="bg-gradient-to-r from-pink-100 to-rose-100 border-2 border-dashed border-pink-300 px-4 py-2 rounded-lg cursor-pointer hover:bg-pink-200 transition-colors"
                        onClick={() => copyCode(coupon.code)}
                      >
                        <span className="font-mono font-bold text-lg text-pink-700">
                          {coupon.code}
                        </span>
                        <Copy className="w-4 h-4 inline ml-2 text-pink-500" />
                      </div>
                      
                      {!coupon.active && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                          Desativado
                        </span>
                      )}
                      
                      {coupon.is_expired && (
                        <span className="px-2 py-1 bg-red-100 text-red-600 rounded text-xs font-medium">
                          Expirado
                        </span>
                      )}
                    </div>

                    {/* Description */}
                    {coupon.description && (
                      <p className="text-gray-600 text-sm mb-3">{coupon.description}</p>
                    )}

                    {/* Details */}
                    <div className="flex flex-wrap gap-4 text-sm">
                      <div className="flex items-center gap-1 text-gray-600">
                        {coupon.discount_type === 'percentage' ? (
                          <>
                            <Percent className="w-4 h-4" />
                            <span className="font-medium text-indigo-600">
                              {coupon.discount_value}% de desconto
                            </span>
                          </>
                        ) : (
                          <>
                            <DollarSign className="w-4 h-4" />
                            <span className="font-medium text-indigo-600">
                              R$ {coupon.discount_value.toFixed(2)} de desconto
                            </span>
                          </>
                        )}
                      </div>

                      {coupon.min_purchase > 0 && (
                        <div className="flex items-center gap-1 text-gray-500">
                          <span>Mínimo: R$ {coupon.min_purchase.toFixed(2)}</span>
                        </div>
                      )}

                      <div className="flex items-center gap-1 text-gray-500">
                        <Hash className="w-4 h-4" />
                        <span>
                          {coupon.uses_count || 0}
                          {coupon.max_uses ? `/${coupon.max_uses}` : ''} usos
                        </span>
                      </div>

                      <div className="flex items-center gap-1 text-gray-500">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(coupon.expires_at)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleToggleActive(coupon)}
                      title={coupon.active ? 'Desativar' : 'Ativar'}
                    >
                      {coupon.active ? (
                        <ToggleRight className="w-5 h-5 text-emerald-600" />
                      ) : (
                        <ToggleLeft className="w-5 h-5 text-gray-400" />
                      )}
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleOpenForm(coupon)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(coupon.coupon_id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingCoupon ? 'Editar Cupom' : 'Novo Cupom'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {/* Code */}
            <div>
              <Label htmlFor="code">Código do Cupom</Label>
              <Input
                id="code"
                value={formData.code}
                onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                placeholder="EX: DESCONTO10"
                className="mt-1 uppercase font-mono"
                maxLength={20}
                disabled={!!editingCoupon}
                data-testid="coupon-code-input"
              />
            </div>

            {/* Discount Type */}
            <div>
              <Label>Tipo de Desconto</Label>
              <div className="flex gap-2 mt-1">
                <Button
                  type="button"
                  variant={formData.discount_type === 'percentage' ? 'default' : 'outline'}
                  className="flex-1"
                  onClick={() => setFormData({...formData, discount_type: 'percentage'})}
                >
                  <Percent className="w-4 h-4 mr-1" />
                  Porcentagem
                </Button>
                <Button
                  type="button"
                  variant={formData.discount_type === 'fixed' ? 'default' : 'outline'}
                  className="flex-1"
                  onClick={() => setFormData({...formData, discount_type: 'fixed'})}
                >
                  <DollarSign className="w-4 h-4 mr-1" />
                  Valor Fixo
                </Button>
              </div>
            </div>

            {/* Discount Value */}
            <div>
              <Label htmlFor="discount_value">
                Valor do Desconto {formData.discount_type === 'percentage' ? '(%)' : '(R$)'}
              </Label>
              <Input
                id="discount_value"
                type="number"
                step="0.01"
                min="0"
                max={formData.discount_type === 'percentage' ? '100' : undefined}
                value={formData.discount_value}
                onChange={(e) => setFormData({...formData, discount_value: e.target.value})}
                placeholder={formData.discount_type === 'percentage' ? '10' : '25.00'}
                className="mt-1"
                data-testid="coupon-value-input"
              />
            </div>

            {/* Min Purchase */}
            <div>
              <Label htmlFor="min_purchase">Valor Mínimo de Compra (R$)</Label>
              <Input
                id="min_purchase"
                type="number"
                step="0.01"
                min="0"
                value={formData.min_purchase}
                onChange={(e) => setFormData({...formData, min_purchase: e.target.value})}
                placeholder="0.00 (sem mínimo)"
                className="mt-1"
              />
            </div>

            {/* Max Uses */}
            <div>
              <Label htmlFor="max_uses">Limite de Usos</Label>
              <Input
                id="max_uses"
                type="number"
                min="1"
                value={formData.max_uses}
                onChange={(e) => setFormData({...formData, max_uses: e.target.value})}
                placeholder="Ilimitado"
                className="mt-1"
              />
            </div>

            {/* Expiration */}
            <div>
              <Label htmlFor="expires_at">Data de Expiração</Label>
              <Input
                id="expires_at"
                type="date"
                value={formData.expires_at}
                onChange={(e) => setFormData({...formData, expires_at: e.target.value})}
                className="mt-1"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">Descrição (opcional)</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Ex: Desconto de boas-vindas"
                className="mt-1"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                disabled={saving}
                className="flex-1 bg-gradient-to-r from-pink-500 to-rose-500"
                data-testid="save-coupon-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                {editingCoupon ? 'Salvar Alterações' : 'Criar Cupom'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowForm(false)}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
